import type { KursDto } from "./kosztyTypy.js";

/**
 * Typy zakladki Developer: historia wywolan modeli (GET /api/developer/...). Wylacznie typy i stale, bez DOM i bez API Node.
 *
 * Listy ZAKRESY_WYWOLAN i STATUSY_WYWOLAN to kopie tego, co serwer zapisuje w kolumnach `scope` i `status` (w schemacie
 * to zwykle String, wiec kompilator nic nie pilnuje). Pilnuje ich tests/unit/wywolaniaTypy.test.ts: nowy zakres w
 * `LlmScope` (src/services/llmLog.ts) bez wpisu tutaj wywala test, zamiast zniknac z filtrow.
 */

export const ZAKRESY_WYWOLAN = [
  "transkrypcja",
  "brzmienie",
  "wymiary",
  "soczewka",
  "podzial",
  "nazwy-mowcow",
  "trening",
  "diagnostyka",
  "porownanie",
] as const;
export type ZakresWywolania = (typeof ZAKRESY_WYWOLAN)[number];

/** WYSLANE (poszlo, nie wrocilo jeszcze), OK, BLAD, PORZUCONE (poszlo i nie wrocilo: restart albo timeout). */
export const STATUSY_WYWOLAN = ["WYSLANE", "OK", "BLAD", "PORZUCONE"] as const;
export type StatusDziennika = (typeof STATUSY_WYWOLAN)[number];

/** Ile wierszy na strone i ile najwyzej. */
export const DOMYSLNY_LIMIT_WYWOLAN = 25;
export const MAKS_LIMIT_WYWOLAN = 100;

/**
 * Najdluzszy tekst w polu "Szukaj": w liscie wywolan i w logach. Serwer odrzuca dluzszy (400 zly_parametr), a pole w
 * interfejsie ma ten sam `maxLength`, wiec uzytkownik nie dochodzi do surowego bledu API przez samo pisanie.
 */
export const MAKS_DLUGOSC_SZUKANIA_WYWOLAN = 100;
export const MAKS_DLUGOSC_SZUKANIA_LOGOW = 200;

/**
 * Od ilu znakow szukany tekst jest tez PREFIKSEM identyfikatora wywolania. Tabela pokazuje skrot (8 znakow), wiec samo
 * rowne id nie wystarcza. Krotszy napis (np. "cm" albo "gem") pasowalby do prefiksu prawie kazdego id (wszystkie cuid
 * zaczynaja sie od "c", a kolejne znaki to czas), wiec taki tekst szuka tylko pelnego id.
 */
export const MIN_DLUGOSC_PREFIKSU_ID = 6;

/** Okno "ostatniej doby" w Stanie systemu. Ta sama wartosc liczy kafelki na serwerze i buduje link do listy w kliencie. */
export const OKNO_STANU_MS = 24 * 60 * 60 * 1000;

/** Okno tresci: ile znakow naraz idzie do przegladarki (pelny prompt podzialu to nawet 400 tys. znakow). */
export const DOMYSLNE_OKNO_TRESCI = 20_000;
export const MAKS_OKNO_TRESCI = 100_000;

/** Koszt jednego wiersza: te same trzy rozstrzygniecia co w src/services/koszty/liczKoszt.ts. */
export type KosztWywolaniaDto =
  /**
   * `dolnaGranica` (powod po polsku) jest ustawiona, gdy kwota to tylko DOLNE OGRANICZENIE: znamy tokeny, ale wiersz nie
   * rozbija wejscia na audio i tekst, a model ma droższa stawke za audio. Taka kwota nie jest pokazywana jako dokladna.
   */
  | { rodzaj: "policzony"; usd: number; dolnaGranica?: string }
  | { rodzaj: "brak"; podstawa: "BRAK_STAWKI" | "BRAK_DANYCH"; powod: string }
  /** Nieudana proba albo wywolanie bez odpowiedzi, bez zuzycia: poza kosztem (dostawca mogl ja naliczyc). */
  | { rodzaj: "wylaczony" };

export interface WywolanieWierszDto {
  id: string;
  /** ISO. */
  utworzono: string;
  zakres: string;
  dostawca: string;
  /** Model UZYTY (po zamknieciu wpisu), a przed odpowiedzia zamowiony. */
  model: string;
  status: string;
  ok: boolean;
  httpStatus: number | null;
  /** finishReason z dziennika: powod zakonczenia u dostawcy albo nasz znacznik (timeout, too_long, model_loading). */
  powodZakonczenia: string | null;
  czasMs: number | null;
  tokenyWejscia: number | null;
  tokenyWyjscia: number | null;
  tokenyRozumowania: number | null;
  tokenyZCache: number | null;
  /** Numer proby w operacji (od 1) i ponowienia schowane wewnatrz jednego wywolania dostawcy. */
  proba: number | null;
  ponowieniaWewnetrzne: number | null;
  sesjaId: string | null;
  probaMikrofonuId: string | null;
  operacjaId: string | null;
  /** Numer wypowiedzi widoczny dla czlowieka (od 1): serwer przelicza indeks z bazy (od 0) przez shared/numeracja. */
  numerWypowiedzi: number | null;
  /** Komunikat bledu po redakcji sekretow (w liscie przyciety). */
  blad: string | null;
  koszt: KosztWywolaniaDto;
}

export interface ListaWywolanDto {
  wiersze: WywolanieWierszDto[];
  /** Ile wierszy pasuje do filtrow (nie tylko na tej stronie). */
  razem: number;
  /**
   * Offset, od ktorego FAKTYCZNIE jest ta strona. Prosba o offset poza zakresem (>= `razem`, gdy `razem` > 0) nie daje
   * pustej listy: serwer przycina go do poczatku ostatniej strony i oddaje ten przyciety offset, wiec klient widzi roznice
   * miedzy tym, o co prosil, a tym, co dostal. Przy `razem` = 0 offset to 0.
   */
  offset: number;
  limit: number;
  kurs: KursDto | null;
  powodBrakuKursu: string | null;
}

export type ZrodloTresci = "ewidencja" | "snapshot" | "ocena";

/**
 * Stan tresci (zapytania albo odpowiedzi) jednego wywolania. Nie udajemy, ze dane sa, gdy ich nie ma, i odrozniamy
 * trzy rozne "nie ma": wygaslo zgodnie z polityka 30 dni, nigdy nie zapisano i wywolanie jeszcze trwa.
 */
export type StanTresciDto =
  | {
      stan: "zapisana";
      zrodlo: ZrodloTresci;
      /** Dlugosc w znakach. */
      znaki: number;
      /** Tresc zapisana w bazie jest przycieta (odpowiedz dluzsza niz limit zapisu). */
      ucieta: boolean;
      sha256: string | null;
      bajty: number | null;
    }
  | { stan: "wygasla"; wygaslo: string; sha256: string | null; bajty: number | null }
  | { stan: "w-toku" }
  | { stan: "niezapisana"; powod: string };

export interface WywolanieSzczegolDto extends WywolanieWierszDto {
  dostawcaZamowiony: string | null;
  modelZamowiony: string | null;
  /** Wersja aplikacji z commitem, z ktorej poszlo wywolanie. */
  wersjaKodu: string | null;
  /** Moment wyslania (createdAt to moment zapisu wpisu). */
  rozpoczeto: string | null;
  endpoint: string | null;
  /** Cialo zadania bez audio i bez promptu (po redakcji sekretow). */
  parametry: unknown;
  /** Wybrane naglowki odpowiedzi z bialej listy. */
  naglowki: Record<string, string> | null;
  audio: {
    sha256: string | null;
    bajty: number | null;
    czasMs: number | null;
    zrodloSha256: string | null;
    wycinekOdMs: number | null;
    wycinekDoMs: number | null;
  } | null;
  zapytanie: StanTresciDto;
  odpowiedz: StanTresciDto;
  /** Inne proby tej samej operacji (bez tej). */
  powiazane: WywolanieWierszDto[];
  sesja: { id: string; tytul: string } | null;
  kurs: KursDto | null;
  powodBrakuKursu: string | null;
}

export type CzescTresci = "zapytanie" | "odpowiedz";

export interface TrescOknoDto {
  czesc: CzescTresci;
  tekst: string;
  offset: number;
  /** Ile znakow w tym oknie. */
  dlugosc: number;
  /** Ile znakow ma cala tresc. */
  razem: number;
  /** Czy za tym oknem sa dalsze znaki. */
  jestDalej: boolean;
}

/** Odpowiedz na prosbe o tresc: stan zawsze, a okno tylko gdy tresc jest zapisana. */
export interface TrescWywolaniaDto {
  stan: StanTresciDto;
  okno: TrescOknoDto | null;
}

export interface StanSystemuDto {
  wersja: string;
  srodowisko: string;
  uptimeSek: number;
  /** Czy ffmpeg i ffprobe da sie uruchomic (bez nich nie ma transkrypcji nagran ani pomiaru brzmienia). */
  ffmpeg: { ffmpeg: boolean; ffprobe: boolean };
  retencjaDni: number;
  /** Ile wpisow o wywolaniach przepadlo w tym procesie (baza niedostepna przy zapisie). */
  lukiZapisu: { liczba: number; pierwsza: string | null; ostatnia: string | null };
  kolejka: {
    zadania: Record<string, number>;
    sesje: Record<string, number>;
    najstarszeCzekajace: { czekaSek: number; sesjaId: string; proby: number } | null;
    porzucone: { id: string; sesjaId: string; bezOdswiezeniaSek: number | null }[];
    progPorzuceniaSek: number;
  } | null;
  /** Co jest USTAWIONE i kto to FAKTYCZNIE policzy (bez wywolywania dostawcow). */
  wybor: Record<
    "transkrypcja" | "brzmienie" | "wymiary",
    { zamowiony: string; policzy: string | null; wersja: string | null; zgodne: boolean; podmienioneZ: string | null }
  > | null;
  /** Dziennik wywolan z ostatnich 24 godzin: ile wierszy i ile z nich to bledy. */
  wywolania24h: { wszystkie: number; bledy: number; porzucone: number } | null;
  /**
   * Ktore bloki nie zostaly policzone (awaria bazy przy tym odczycie). Pole null w bloku to NIE "pusto" ani "zero": to
   * brak odpowiedzi, wiec interfejs mowi to wprost zamiast pokazywac zera.
   */
  niedostepne: ("kolejka" | "wybor" | "wywolania24h")[];
  wygenerowano: string;
}

export interface LogiDto {
  plik: string;
  istnieje: boolean;
  bajty: number;
  oknoObciete: boolean;
  poziom: string | null;
  szukaj: string | null;
  wOknie: number;
  pasujacych: number;
  linie: string[];
  wygenerowano: string;
}

export interface WynikSondyDto {
  dostawcy: {
    rodzaj: string;
    id: string;
    etykieta: string;
    model: string;
    stan: "odpowiada" | "blad" | "nieskonfigurowany";
    szczegol: string;
    czasMs: number | null;
  }[];
  /** Ile dostawcow faktycznie odpytano (bez nieskonfigurowanych). Czesc z tych zapytan jest platna. */
  wyslano: number;
  wygenerowano: string;
}

/**
 * Co zrobi sonda (do okna potwierdzenia): ilu skonfigurowanych dostawcow zostanie odpytanych i ile z tych zapytan jest
 * PLATNYCH (wywolanie modelu), a ile to darmowe sprawdzenie lacznosci (lista modeli, /health).
 */
export interface PodgladSondyDto {
  doWyslania: number;
  platnych: number;
  dostawcy: { rodzaj: string; id: string; etykieta: string; skonfigurowany: boolean; platny: boolean }[];
}
